import { Alert, Skeleton } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import * as api from "../Messages/api/services.ts"
import { ConversationView } from "../Messages/components/ConversationView.tsx"
import { useOpenConversations } from "../Messages/hooks/use-open-conversations.ts"
import { upsertConversation } from "../Messages/redux/messaging.slice.ts"
import type { Conversation } from "../Messages/types/messaging.types.ts"
import { PortalEmptyState } from "./components/PortalEmptyState.tsx"
import { PortalPageContainer } from "./components/PortalPageContainer.tsx"
import { PortalPageHeader } from "./components/PortalPageHeader.tsx"
import { PortalSection } from "./components/PortalSection.tsx"

/**
 * The customer's messages.
 *
 * One conversation per project, and only the customer-facing one: the portal
 * never learns that an internal room or a group exists, let alone its name or
 * unread count.
 */
function PortalMessagesPage() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const projects = useAppSelector((state) => state.portal.projects)
  const conversations = useAppSelector((state) => state.messaging.conversations)

  const [loading, setLoading] = useState(true)
  const [ids, setIds] = useState<string[]>([])

  const projectId = searchParams.get("project")
  const visibleProjects = useMemo(
    () => (projectId ? projects.filter((p) => p.id === projectId) : projects),
    [projects, projectId]
  )

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      const found: string[] = []
      for (const project of visibleProjects) {
        const response = await api.getPortalProjectConversation(project.id)
        if (response.ok && response.data?.conversation) {
          dispatch(upsertConversation(response.data.conversation))
          found.push(response.data.conversation.id)
        }
      }
      if (cancelled) return
      setIds(found)
      setLoading(false)
    }
    if (visibleProjects.length) load()
    else setLoading(false)
    return () => {
      cancelled = true
    }
  }, [visibleProjects, dispatch])

  useOpenConversations(ids)

  const rooms = useMemo(
    () =>
      ids
        .map((id) => conversations.find((row) => row.id === id))
        .filter(Boolean) as Conversation[],
    [ids, conversations]
  )

  return (
    <PortalPageContainer className={"max-w-5xl"}>
      <PortalPageHeader
        title={t("portal.messages.title")}
        subtitle={t(
          "portal.messages.subtitle",
          "Talk to your project manager about your project."
        )}
      />

      {loading && <Skeleton active paragraph={{ rows: 6 }} />}

      {!loading && visibleProjects.length === 0 && (
        <PortalEmptyState
          title={t("portal.home.noProjectsTitle")}
          description={t("portal.home.noProjectsDescription")}
        />
      )}

      {!loading && visibleProjects.length > 0 && rooms.length === 0 && (
        <Alert
          type={"info"}
          showIcon
          message={t(
            "portal.messages.notReady",
            "Your project manager has not opened a conversation yet. You will see it here when they do."
          )}
        />
      )}

      {!loading &&
        rooms.map((conversation) => (
          <PortalSection key={conversation.id} title={conversation.record_name}>
            <div className={"h-[32rem] overflow-hidden rounded-lg border border-gray-200"}>
              <ConversationView conversation={conversation} portal />
            </div>
          </PortalSection>
        ))}
    </PortalPageContainer>
  )
}

export default PortalMessagesPage
